import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import { authService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const AuthForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    return (
      password?.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password)
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * แปลง username → { first_name, last_name }
   * กฎ: ตัดที่ . หรือ _ ตัวแรกที่เจอ
   *   - ก่อนหน้า  → first_name
   *   - หลังจาก strip delimiter ติดกัน → เอา segment ถัดไปจนถึง . หรือ _ ตัวต่อไป → last_name
   *   - ถ้าไม่มี . หรือ _ เลย → first_name = username, last_name = null
   *
   * ตัวอย่าง:
   *   mynameistest      → first=mynameistest  last=null
   *   myname.istest     → first=myname        last=istest
   *   myname_istest     → first=myname        last=istest
   *   my.name_istest    → first=my            last=name
   *   my_name.istest    → first=my            last=name
   *   my..name_istest   → first=my            last=name
   *   my___.nameis.test → first=my            last=nameis
   */
  const parseUsername = (username) => {
    const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
    const stripNums = (str) => str ? str.replace(/[0-9]/g, '') : str;
    const match = username.match(/[._]/);
    if (!match) {
      const name = stripNums(username);
      return { first_name: name ? capitalize(name) : null, last_name: null };
    }
    const rawFirst = stripNums(username.substring(0, match.index));
    const first_name = rawFirst ? capitalize(rawFirst) : null;
    const rest = username.substring(match.index + 1).replace(/^[._]+/, '');
    const lastMatch = rest.match(/^([^._]+)/);
    const rawLast = lastMatch ? stripNums(lastMatch[1]) : null;
    const last_name = rawLast ? capitalize(rawLast) : null;
    return { first_name: first_name || null, last_name: last_name || null };
  };

  const handleUsernameChange = (e) => {
    const raw = e?.target?.value?.replace(/\s/g, '') || '';
    setFormData(prev => ({ ...prev, username: raw }));
    if (errors?.username) setErrors(prev => ({ ...prev, username: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData?.username?.trim().length < 6) {
      newErrors.username = 'Username must be at least 6 characters';
    } else if (!/^[a-zA-Z0-9._]+$/.test(formData?.username?.trim())) {
      newErrors.username = 'Only a-z, 0-9, . and _ are allowed';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData?.password)) {
      newErrors.password = 'Must be at least 8 characters with uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.confirmPassword !== formData?.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Auto-lowercase before submit
    const cleanUsername = formData?.username?.trim().toLowerCase();
    setFormData(prev => ({ ...prev, username: cleanUsername }));

    setLoading(true);
    setErrors({});

    try {
      {
        // เช็ค username ซ้ำก่อน signUp (ใช้ RPC เพราะ RLS block unauthenticated)
        const { data: existingEmail } = await supabase.rpc('get_email_by_username', {
          p_username: cleanUsername,
        });

        if (existingEmail) {
          setErrors({ username: 'Username already taken' });
          setLoading(false);
          return;
        }

        // Registration
        const { data, error } = await authService?.signUp(
          formData?.email,
          formData?.password,
          {
            ...parseUsername(formData?.username || ''),
            username: formData?.username,
            subscription_tier: 'free'
          }
        );

        if (error) {
          const msg = error?.message?.toLowerCase() || '';
          if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('duplicate') || msg.includes('already exists')) {
            setErrors({ email: 'Email already registered' });
          } else if (msg.includes('database error') || msg.includes('username')) {
            setErrors({ username: 'Username already taken' });
          } else {
            setErrors({ email: error?.message || 'Registration failed. Please try again.' });
          }
          setLoading(false);
          return;
        }

        if (data?.user) {
          // Set session marker cookie — ป้องกัน AuthContext force-logout หลัง full page reload
          // (เช่น return จาก Stripe Checkout). Session-scoped (ไม่มี Max-Age) → หายเมื่อปิด browser.
          const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
          const secureFlag = isHttps ? '; Secure' : '';
          document.cookie = `session_active=1; path=/; SameSite=Lax${secureFlag}`;

          // สร้าง verification code 8 ตัว
          const { data: code, error: codeErr } = await supabase.rpc('generate_email_code', { p_email: formData.email });

          if (codeErr) {
            setErrors({ email: 'Account created but failed to send verification email. Please try again.' });
            setLoading(false);
            return;
          }

          // ส่ง email ผ่าน Edge Function
          await supabase.functions.invoke('send-verification-email', {
            body: { email: formData.email, code },
          });

          // เก็บ email ไว้สำหรับหน้า verify (กันกรณี refresh)
          localStorage.setItem('pending_verification_email', formData.email);
          localStorage.removeItem('resend_cooldown_at');

          navigate('/email-verification', {
            state: {
              email: formData.email,
            },
          });
        }
      }
    } catch (error) {
      setErrors({ email: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
          Create Your Space
        </h1>
      </div>

      {/* Toggle between Login/Register */}
      <div className="mb-8">
        <p className="text-sm text-gray-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-white hover:text-gray-300 font-medium underline transition-colors"
          >
            LOG IN
          </button>
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Username */}
        <div>
          <input
            type="text"
            name="username"
            placeholder="Username"
            value={formData?.username}
            onChange={handleUsernameChange}
            autoComplete="username"
            className={`w-full px-4 py-3.5 bg-[#1a1a1a] border rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] ${errors?.username ? 'border-red-500' : 'border-[#2a2a2a]'}`}
          />
          {errors?.username && (
            <p className="text-red-400 text-xs mt-1.5">{errors?.username}</p>
          )}
        </div>

        <div>
          <input
            type="email"
            name="email"
            placeholder={'Email'}
            value={formData?.email}
            onChange={handleInputChange}
            className={`w-full px-4 py-3.5 bg-[#1a1a1a] border rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] ${errors?.email ? 'border-red-500' : 'border-[#2a2a2a]'}`}
          />
          {errors?.email && (
            <p className="text-red-400 text-xs mt-1.5">{errors?.email}</p>
          )}
        </div>

        <div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Password"
              value={formData?.password}
              onChange={handleInputChange}
              className="w-full px-4 py-3.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 pr-12 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)]"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {errors?.password && (
            <p className="text-red-400 text-xs mt-1.5">{errors?.password}</p>
          )}
          {!errors?.password && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
              <p className={`text-xs flex items-center gap-1.5 ${formData?.password?.length >= 8 ? 'text-green-400' : 'text-gray-500'}`}>
                <span>{formData?.password?.length >= 8 ? '✓' : '–'}</span> At least 8 characters
              </p>
              <p className={`text-xs flex items-center gap-1.5 ${/[A-Z]/.test(formData?.password) ? 'text-green-400' : 'text-gray-500'}`}>
                <span>{/[A-Z]/.test(formData?.password) ? '✓' : '–'}</span> Uppercase (A–Z)
              </p>
              <p className={`text-xs flex items-center gap-1.5 ${/[a-z]/.test(formData?.password) ? 'text-green-400' : 'text-gray-500'}`}>
                <span>{/[a-z]/.test(formData?.password) ? '✓' : '–'}</span> Lowercase (a–z)
              </p>
              <p className={`text-xs flex items-center gap-1.5 ${/[0-9]/.test(formData?.password) ? 'text-green-400' : 'text-gray-500'}`}>
                <span>{/[0-9]/.test(formData?.password) ? '✓' : '–'}</span> Number (0–9)
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData?.confirmPassword}
              onChange={handleInputChange}
              className={`w-full px-4 py-3.5 bg-[#1a1a1a] border rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 pr-12 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] ${errors?.confirmPassword ? 'border-red-500' : formData?.confirmPassword && formData?.confirmPassword === formData?.password ? 'border-green-500' : 'border-[#2a2a2a]'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="text-gray-400 hover:text-gray-300 transition-colors"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>
          </div>
          {errors?.confirmPassword ? (
            <p className="text-red-400 text-xs mt-1.5">{errors?.confirmPassword}</p>
          ) : formData?.confirmPassword && formData?.confirmPassword === formData?.password ? (
            <p className="text-green-400 text-xs mt-1.5 flex items-center gap-1">
              <span>✓</span> Passwords match
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          className="mt-6 bg-[#2a2a2a] hover:bg-[#333333] text-white border border-[#2a2a2a] rounded-2xl py-3.5 font-medium transition-all shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] focus:outline-none focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] active:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)]"
        >
          <span className="flex items-center justify-center gap-2">
            Start Creating
            <div className="w-8 h-8 rounded-full bg-[#3a3a3a] flex items-center justify-center">
              <Icon name="ArrowRight" size={16} className="text-white" />
            </div>
          </span>
        </Button>
      </form>

      {/* Terms */}
      <div className="mt-6">
        <p className="text-xs text-gray-500 leading-relaxed">
          By signing up, you agree to Calendly's{' '}
          <a href="#" className="text-gray-400 hover:text-gray-300 underline">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-gray-400 hover:text-gray-300 underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default AuthForm;